import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasPremiumFeature, type BoxInfo, type CardInfo } from '@lub0v/santa-utils';
import { Button, Hint, Textarea, showToast } from '@src/components';
import { usePremiumModal } from '@src/components/PremiumUpsell/PremiumModal';
import { LockIcon } from '@src/components/icons/LockIcon';
import { PaperPlaneIcon } from '@src/components/icons/PaperPlaneIcon';
import { pushDialog } from '@src/components/Dialog/Dialog';
import { ParticipantFace } from '@src/components/faces/ParticipantFace';
import { Typography } from '@src/components/Typography/Typography';
import { trackEvent } from '@src/utils/analytics';
import { trackUmamiEvent } from '@src/utils/umami';
import './BulkReminder.css';

export type ReminderContext = 'general' | 'opened' | 'filled' | 'revealed' | 'sent' | 'received';
type Recipient = CardInfo;
export type ReminderChannels = { id: number; hasEmail: boolean; email: boolean; telegram: boolean; vk: boolean };
type BindSubmit = (send: () => Promise<void>) => void;

export type ReminderRecipient = Pick<CardInfo, 'id' | 'username' | 'picture' | 'avatar' | 'frameName'>;

/** Shared by real reminders and the local-only Premium page example. */
export function ReminderComposerFields({ recipients, channels, channelsFailed = false, message, onMessageChange, disabled = false, showHints = true, rows = 5, scrollRecipients = true }: {
  recipients: ReminderRecipient[]; channels: ReminderChannels[]; channelsFailed?: boolean; message: string; onMessageChange: (value: string) => void; disabled?: boolean; showHints?: boolean; rows?: number; scrollRecipients?: boolean;
}) {
  const { t } = useTranslation();
  return <>
    <section className="bulk-reminder__recipients" aria-label={t('bulkReminder.recipients', { count: recipients.length })}>
      <Typography as="p" role="body/strong">{t('bulkReminder.recipients', { count: recipients.length })}</Typography>
      <ul className={scrollRecipients ? undefined : 'bulk-reminder__recipient-list--expanded'} tabIndex={scrollRecipients ? 0 : undefined} aria-label={t('bulkReminder.recipients', { count: recipients.length })}>{recipients.map((person, index) => {
        const info = channels.find(channel => channel.id === person.id);
        const label = t(!info ? channelsFailed ? 'bulkReminder.channelsUnavailable' : 'bulkReminder.channelsLoading' : info.hasEmail ? info.email ? 'bulkReminder.emailEnabled' : 'bulkReminder.emailDisabled' : 'bulkReminder.siteOnly');
        return <li key={person.id}><ParticipantFace card={person} index={index} size="row" /><div>
          <Typography as="span" role="body/strong">{person.username}</Typography>
          <div className="bulk-reminder__channels">{info && !info.hasEmail ? <Hint tone="error">{label}</Hint> : <span>{label}</span>}</div>
        </div></li>;
      })}</ul>
      {showHints && <p className="bulk-reminder__recipient-note">{t('bulkReminder.individual')}</p>}
    </section>
    <Textarea label={t('bulkReminder.message')} value={message} onValueChange={onMessageChange} disabled={disabled} maxLength={1000} showCount rows={rows} testId="bulk-reminder-message" />
    {showHints && <Hint tone="info">{t(channels.some(channel => !channel.hasEmail) ? 'bulkReminder.deliveryHintMissingEmail' : 'bulkReminder.deliveryHint')}</Hint>}
  </>;
}

function ReminderComposer({ boxKey, recipients, prefill, bindSubmit, busy, setDisabled, onSent }: {
  boxKey: string; recipients: Recipient[]; prefill?: 'filled' | 'sent'; bindSubmit: BindSubmit;
  busy: boolean; setDisabled: (value: boolean) => void; onSent: (ids: number[]) => void;
}) {
  const { t } = useTranslation();
  const [channels, setChannels] = useState<ReminderChannels[]>([]);
  const [channelsFailed, setChannelsFailed] = useState(false);
  useEffect(() => {
    const abort = new AbortController();
    const load = async () => {
      const result: ReminderChannels[] = [];
      for (let offset = 0; offset < recipients.length; offset += 25) {
        const response = await fetch(`/api/box/${encodeURIComponent(boxKey)}/reminder-channels`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: abort.signal,
          body: JSON.stringify({ cardIds: recipients.slice(offset, offset + 25).map(person => person.id) }),
        });
        if (!response.ok) throw new Error('channels');
        result.push(...await response.json());
      }
      setChannels(result);
    };
    load().catch(() => { if (!abort.signal.aborted) setChannelsFailed(true); });
    return () => abort.abort();
  }, [boxKey, recipients]);
  const [message, setMessage] = useState(() => prefill ? t(`bulkReminder.templates.${prefill}`) : '');
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState<number[]>([]);
  const [sentCount, setSentCount] = useState(0);
  const batches = useRef<Array<{ requestId: string; cardIds: number[]; done: boolean }> | null>(null);
  const notified = useRef(new Set<number>());
  const [started, setStarted] = useState(false);
  useEffect(() => setDisabled(!message.trim() || message.length > 1000 || uncertain.length > 0), [message, uncertain.length, setDisabled]);
  const send = useCallback(async () => {
    if (!message.trim() || uncertain.length) return;
    setError('');
    setStarted(true);
    if (!batches.current) batches.current = Array.from({ length: Math.ceil(recipients.length / 25) }, (_, index) => ({ requestId: crypto.randomUUID(), cardIds: recipients.slice(index * 25, (index + 1) * 25).map(person => person.id), done: false }));
    try {
      for (const batch of batches.current) {
        if (batch.done) continue;
        const response = await fetch(`/api/box/${encodeURIComponent(boxKey)}/reminders`, {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ requestId: batch.requestId, cardIds: batch.cardIds, message: message.trim() }),
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          const code = body.error?.message || body.message;
          throw new Error(code === 'PREMIUM_REQUIRED' ? 'premiumRequired' : code === 'INVALID_REMINDER_RECIPIENTS' ? 'recipientsChanged' : 'failed');
        }
        const result = await response.json() as { sent: number[]; uncertain: number[] };
        const delivered = result.sent.filter(id => !notified.current.has(id));
        delivered.forEach(id => notified.current.add(id));
        setSentCount(notified.current.size);
        if (delivered.length) onSent(delivered);
        if (result.uncertain.length) {
          setUncertain(result.uncertain);
          throw new Error('uncertain');
        }
        batch.done = true;
      }
      showToast(t('bulkReminder.success', { count: notified.current.size }), 'success');
    } catch (failure) {
      const key = failure instanceof Error && ['premiumRequired', 'recipientsChanged', 'uncertain'].includes(failure.message) ? failure.message : 'failed';
      setError(t(`bulkReminder.${key}`));
      throw failure;
    }
  }, [boxKey, message, onSent, recipients, t, uncertain.length]);
  useEffect(() => bindSubmit(send), [bindSubmit, send]);
  return <div className="bulk-reminder" data-testid="bulk-reminder-composer">
    <ReminderComposerFields recipients={recipients} channels={channels} channelsFailed={channelsFailed} message={message} onMessageChange={setMessage} disabled={busy || started} />
    {started && <p role="status">{t('bulkReminder.progress', { sent: sentCount, total: recipients.length })}</p>}
    {error && <Hint tone="error">{error}{uncertain.length > 0 && <span> {recipients.filter(person => uncertain.includes(person.id)).map(person => person.username).join(', ')}</span>}</Hint>}
  </div>;
}

export function BulkReminderButton({ box, recipients, prefill, onSent, disabled = false, size = 'large' }: {
  size?: 'small' | 'medium' | 'large'; box: BoxInfo['box']; recipients: Recipient[]; context?: ReminderContext; prefill?: 'filled' | 'sent'; onSent: (ids: number[]) => void; disabled?: boolean;
}) {
  const { t } = useTranslation();
  const openPremium = usePremiumModal();
  const allowed = recipients.length === 1 || hasPremiumFeature(box, 'boxActivityLog');
  return <Button size={size} variant={allowed ? 'primary' : 'premium-light'} icon={allowed ? <PaperPlaneIcon /> : <LockIcon />} disabled={disabled || box.isArchived || !recipients.length}
    testId="bulk-reminder-open" onClick={() => {
      const data = { locked: !allowed, recipients: recipients.length, context: prefill ?? 'general' };
      try { trackEvent('bulk_reminder_clicked', data); } catch { /* Analytics must not block reminders. */ }
      try { void Promise.resolve(trackUmamiEvent('bulk_reminder_clicked', data)).catch(() => {}); } catch { /* Analytics is optional. */ }
      if (!allowed) { openPremium('history', undefined, 'bulk_reminder', 'learn_more', { comparison: true }); return; }
      let submit = async () => {};
      const bindSubmit: BindSubmit = send => { submit = send; };
      const selectedRecipients = [...recipients];
      pushDialog({ title: t('bulkReminder.title'), mobileFullScreen: true, testId: 'bulk-reminder-dialog', cancel: t('box.cancel'), submit: t('bulkReminder.send', { count: recipients.length }),
        renderBody: (busy, setDisabled) => <ReminderComposer boxKey={box.key} recipients={selectedRecipients} prefill={prefill} bindSubmit={bindSubmit} busy={busy} setDisabled={setDisabled} onSent={onSent} />,
        onSubmit: () => submit(),
      });
    }}>{t(allowed ? 'bulkReminder.action' : 'bulkReminder.lockedAction', { count: recipients.length })}</Button>;
}
